"use client";

import { useIsRestoring, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAccessToken } from "@/hooks/shared";
import type { MediaGroup } from "@/openapi/generated/models";
import {
  fetchMediaAsset,
  type MediaOwnerType,
} from "@/lib/utils/media/fetch-media-asset";
import {
  PUBLIC_PRESIGNED_URL_GC_MS,
  PUBLIC_PRESIGNED_URL_STALE_MS,
} from "@/lib/utils/media/public-media-cache";
import {
  getMediaAssetQueryKey,
  mediaAssetQueryScope,
} from "@/lib/utils/media/media-asset-query-key";

export type { MediaOwnerType };

export interface UseMediaAssetGetProps {
  tenantId: number;
  ownerType: MediaOwnerType;
  ownerId: number;
  mediaGroup: MediaGroup;
}

export type { MediaAssetResult } from "@/lib/utils/media/fetch-media-asset";

/** Presigned media URLs from the API typically expire in ~1 hour. */
const AUTHENTICATED_MEDIA_STALE_MS = 5 * 60 * 1000;

export const useMediaAssetGet = (
  { tenantId, ownerType, ownerId, mediaGroup }: UseMediaAssetGetProps,
  options?: { enabled?: boolean; refetchOnMount?: boolean | "always"; requireAuth?: boolean },
) => {
  const accessToken = useAccessToken();
  const isRestoring = useIsRestoring();
  
  // By default, require authentication. Set requireAuth: false for public endpoints
  const requireAuth = options?.requireAuth !== false;
  const isPublicMedia = !requireAuth;

  // Ensure tenantId and ownerId are valid numbers (not 0 or negative)
  // This prevents query key changes that could cause infinite loops
  const isValid = tenantId > 0 && ownerId > 0;

  // Stabilize query key to prevent changes during render
  const queryKey = useMemo(
    () =>
      getMediaAssetQueryKey(
        tenantId,
        ownerType,
        ownerId,
        mediaGroup,
        mediaAssetQueryScope(requireAuth),
      ),
    [tenantId, ownerType, ownerId, mediaGroup, requireAuth],
  );

  // Stabilize enabled flag to prevent query from being enabled/disabled repeatedly
  // which could cause infinite loops in production
  const enabled = useMemo(() => {
    if (isRestoring) return false;
    // If auth is required, also check for accessToken
    if (requireAuth) {
      return options?.enabled !== false && !!accessToken && isValid;
    }
    // If auth is not required, just check enabled and isValid
    return options?.enabled !== false && isValid;
  }, [options?.enabled, accessToken, isValid, requireAuth, isRestoring]);

  const { data, isError, isLoading, isSuccess, isPending, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: () => {
      if (requireAuth && !accessToken) {
        throw new Error("Authentication token is required");
      }

      return fetchMediaAsset(
        { tenantId, ownerType, ownerId, mediaGroup },
        accessToken ? { accessToken } : undefined,
      );
    },
    enabled,
    // PR1: rely on SSR prefetch + invalidation; callers can opt into refetchOnMount.
    refetchOnMount: options?.refetchOnMount ?? false,
    // Public URLs expire in ~1h. Focus refetch only runs when stale (25m), so
    // short browse sessions do not hit origin; long-lived tabs get a fresh URL.
    refetchOnWindowFocus: isPublicMedia,
    retry: 1,
    staleTime: isPublicMedia ? PUBLIC_PRESIGNED_URL_STALE_MS : AUTHENTICATED_MEDIA_STALE_MS,
    gcTime: isPublicMedia ? PUBLIC_PRESIGNED_URL_GC_MS : 10 * 60 * 1000,
  });

  return {
    data,
    url: data?.url ?? null,
    mediaId: data?.mediaId ?? null,
    media: data?.media ?? null,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
  };
};

